import {immerable} from "immer";
import ExtractionSheetArticleNodeDto from "@/models/interfaces/dtos/extraction/extraction-sheet-article-node-dto.ts";
import ExtractionSheetLeafNodeDto from "@/models/interfaces/dtos/extraction/extraction-sheet-leaf-node-dto.ts";
import ExtractionNodeType from "@/models/interfaces/dtos/extraction/extraction-node-type.ts";
import ProjectMeasures from "@/pages/takeoff/context/project-measure/models/project-measures.ts";
import MeasureType from "@/pages/takeoff/context/project-measure/models/measure-type.ts";
import ExtractionNodeElement from "./interfaces/extraction-node-element.ts";
import ExtractionSheetArticleNode from "./extraction-sheet-article-node.ts";
import ExtractionSheetArticleElementBase
    from "@/pages/takeoff/layouts/extraction/context/models/article-elements/extraction-sheet-article-element-base.ts";
import ProjectIndicatorsDto from "@/models/interfaces/dtos/indicators/project-indicators-dto.ts";

class ExtractionSheetLeafNode implements ExtractionNodeElement {
    [immerable] = true;

    id: string;

    name: string;

    children: ExtractionNodeElement[];

    constructor(name: string) {
        this.id = crypto.randomUUID();
        this.name = name;
        this.children = [];
    }

    static fromDto(dto: ExtractionSheetLeafNodeDto, projectMeasures: ProjectMeasures, measureTypes: MeasureType[], projectIndicatorsData?: ProjectIndicatorsDto): ExtractionSheetLeafNode {
        const node = new ExtractionSheetLeafNode(dto.name);
        if(dto.children)
            node.children = dto.children.map(childDto => {
                if (childDto.type === ExtractionNodeType.LEAF) {
                    return ExtractionSheetLeafNode.fromDto(childDto as ExtractionSheetLeafNodeDto, projectMeasures, measureTypes, projectIndicatorsData);
                }
                return ExtractionSheetArticleNode.fromDto(childDto as ExtractionSheetArticleNodeDto, projectMeasures, measureTypes, projectIndicatorsData);
            });
        return node;
    }
    
    updateName(name: string) {
        this.name = name;
    }

    /* NODES */
    
    addChildrenNode(childName: string) {
        const childNode = new ExtractionSheetLeafNode(childName);
        this.children.push(childNode);
    }

    removeChildrenNode(nodeId: string) {
        this.children = this.children.filter(child => child.id !== nodeId);
        this.children.forEach(child => {
            if (child instanceof ExtractionSheetLeafNode) {
                child.removeChildrenNode(nodeId);
            }
        });
    }
    
    /* ARTICLES */
    
    addArticle(articleName: string) {
        const article = new ExtractionSheetArticleNode(articleName);
        this.children.push(article);
    }
    
    removeArticle(articleId: string) {
        this.children = this.children.filter(child => child.id !== articleId);
        this.children.forEach(child => {
            if (child instanceof ExtractionSheetLeafNode) {
                child.removeArticle(articleId);
            }
        });
    }
    
    /* Helpers */
    
    findElementById(id: string): ExtractionNodeElement | undefined {
        if (this.id === id) {
            return this;
        }

        for (let i = 0; i < this.children.length; i += 1) {
            const child = this.children[i];
            if (child.id === id) {
                return child;
            }

            if (child instanceof ExtractionSheetLeafNode) {
                const found = child.findElementById(id);
                if (found) {
                    return found;
                }
            }
            
            if(child instanceof ExtractionSheetArticleNode) {
                const found = child.findElementById(id);
                if (found) {
                    return found;
                }
            }
        }

        return undefined;
    }

    getHierarchy(id: string): string[] | undefined {
        if (this.id === id) {
            return [this.name];
        }

        for (let i = 0; i < this.children.length; i += 1) {
            const child = this.children[i];

            if (child instanceof ExtractionSheetLeafNode) {
                const path = child.getHierarchy(id);
                if (path) {
                    return [this.name, ...path];
                }
            }

            if (child instanceof ExtractionSheetArticleNode) {
                if (child.id === id) {
                    return [this.name, child.name];
                }
                const found = child.findElementById(id);
                if (found) {
                    return [this.name, child.name];
                }
            }
        }

        return undefined;
    }

    getNodeArticlesElements(): ExtractionSheetArticleElementBase[] {
        const elements: ExtractionSheetArticleElementBase[] = [];
        for (let i = 0; i < this.children.length; i += 1) {
            const child = this.children[i];

            if (child instanceof ExtractionSheetLeafNode) {
                elements.push(...child.getNodeArticlesElements());
            }

            if (child instanceof ExtractionSheetArticleNode) {
                elements.push(...child.children);
            }
        }

        return elements;
    }
}

export default ExtractionSheetLeafNode;